import { isEmpty } from 'lodash'
import { CombinedGraphQLErrors } from '@apollo/client'
import { EMAIL_REGEX } from 'constants/regex'

export function validateSsn({ ssn, skip }: { ssn: string; skip?: boolean }) {
  const errors: {
    ssn?: string
  } = {}

  const normalized = normalizeSsn(ssn)

  if (!skip && isEmpty(normalized))
    errors['ssn'] = 'Social Security Number is required'
  if (normalized && normalized.length < 9) {
    errors['ssn'] = 'Social Security Number must be 9 digits'
  }

  return errors
}

export function normalizeSsn(ssn: string) {
  return ssn.replace(/[^0-9]/g, '')
}

/**
 * Apollo Client 4 no longer wraps failures in `ApolloError`: GraphQL errors
 * arrive as a `CombinedGraphQLErrors` carrying `errors`, and anything else
 * (network, parse, link) as an ordinary Error. Callers used to reach straight
 * for `error.graphQLErrors`, which no longer exists, so every read of the
 * GraphQL error list goes through here.
 *
 * The entries are `GraphQLFormattedError`, but Absinthe also passes through any
 * extra keys on an `{:error, %{...}}` tuple (e.g. the `id` that
 * `EnrollmentSubmitFund` switches on), so callers may need to cast for those.
 */
export const graphQLErrorsOf = (error: unknown) =>
  CombinedGraphQLErrors.is(error) ? error.errors : []

export function graphQLErrorHandler(
  setter: (arg0: string) => void,
  cb?: () => void
) {
  return (error: unknown) => {
    const graphQLErrors = graphQLErrorsOf(error)

    if (graphQLErrors.length > 0) {
      if (graphQLErrors.find((err) => err.message.includes('maintenance'))) {
        const root = window as any
        root.location.assign('/maintenance')
      } else {
        const message = graphQLErrors.map(({ message }) => message).join(', ')
        // TODO: fix the "Guest YES" hack below when possible
        setter(message.replace(/Guest YES - /, ''))
      }
    } else {
      setter(String(error))
    }
    cb && cb()
  }
}

export function graphQLErrorMessage(error: unknown) {
  if (!error) return ''

  const graphQLErrors = graphQLErrorsOf(error)

  if (graphQLErrors.length > 0) {
    return graphQLErrors.map(({ message }) => message).join(', ')
  }

  return error instanceof Error ? error.message : String(error)
}

export function validateEmail(email: string) {
  return EMAIL_REGEX.test(String(email).toLowerCase())
}

export const trueCharacterLength = (string: string) => {
  return string.match(/./gu)?.length || 0
}

export const isAllEmojis = (string: string) => {
  const emojisRegExp =
    /^(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])+$/
  return emojisRegExp.test(string.replace(/\s/g, '')) === true
}

export const validateFundName = (fundName: string): string | undefined => {
  let error

  if (fundName.length === 0)
    error = 'Fund name is required'
  else if (fundName.length > 255)
    error = 'Fund name must be less than 255 characters long'
  else if (isAllEmojis(fundName))
    error = 'Try adding more than just emojis.'
  else if (trueCharacterLength(fundName) < 5)
    error = 'Make your fund name at least 5 characters.'

  return error
}